// src/frontend/src/components/Simulation/ConversationChat.tsx - Fixed backend URL issue
import React, { useState, useRef, useEffect } from 'react';
import { Conversation, Message, SpeakerRole } from '../../types/simulation';
import { conversationService } from '../../api/simulationApi';

interface ConversationChatProps {
  conversation: Conversation;
  onSendMessage: (message: string) => Promise<void>;
  isLoading: boolean;
}

const ConversationChat: React.FC<ConversationChatProps> = ({ 
  conversation, 
  onSendMessage,
  isLoading
}) => {
  const [message, setMessage] = useState('');
  const [coachingEnabled, setCoachingEnabled] = useState(false);
  const [coachingTip, setCoachingTip] = useState<string | null>(null);
  const [isLoadingTip, setIsLoadingTip] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track user scrolling behavior
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);

  // 🔧 FIXED: Get the correct backend URL
  const getBackendUrl = () => {
    // In development, explicitly use the backend port
    if (process.env.NODE_ENV === 'development') {
      return 'http://localhost:5050';
    }
    // In production, you might have a different setup
    return process.env.REACT_APP_API_URL || '';
  };
  
  // Auto-scroll only when appropriate
  useEffect(() => {
    // Only auto-scroll if user is near bottom or hasn't manually scrolled
    if (isNearBottom && !userHasScrolled) {
      scrollToBottom();
    }
  }, [conversation.messages, streamingResponse, isNearBottom, userHasScrolled]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Improved scroll detection
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;
      
      // Consider "near bottom" if within 100px of the bottom
      const nearBottom = scrollBottom < 100;
      setIsNearBottom(nearBottom);
      
      // If user scrolls up significantly, mark as manual scroll
      if (scrollBottom > 150) {
        setUserHasScrolled(true);
      } else if (nearBottom) {
        // Reset manual scroll flag when user scrolls back to bottom
        setUserHasScrolled(false);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    if (messagesEndRef.current && chatContainerRef.current) {
      // Use a more gentle scroll approach
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  // Force scroll to bottom (when user sends a message)
  const forceScrollToBottom = () => {
    if (messagesEndRef.current) {
      setUserHasScrolled(false);
      setIsNearBottom(true);
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  // 🔧 FIXED: Proper streaming implementation with correct backend URL
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !isLoading && !isStreaming) {
      const currentMessage = message;
      setMessage('');
      setCoachingTip(null);
      
      // Force scroll when user sends a message
      forceScrollToBottom();
      
      // Show loading state
      setIsStreaming(true);
      setStreamingResponse('');
      
      try {
        // Get auth token from localStorage or context
        const token = localStorage.getItem('token');
        const backendUrl = getBackendUrl();
        
        // 🔧 FIXED: Use proper backend URL for streaming
        const response = await fetch(`${backendUrl}/conversations/${conversation._id}/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ message: currentMessage }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullResponse = '';

        if (reader) {
          // Read the streaming response
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  
                  if (data.type === 'status') {
                    setStreamingResponse('Claude is thinking...');
                  } else if (data.type === 'chunk') {
                    fullResponse += data.content;
                    setStreamingResponse(fullResponse);
                    
                    // Only auto-scroll during streaming if user is near bottom
                    if (isNearBottom && !userHasScrolled) {
                      setTimeout(scrollToBottom, 50);
                    }
                  } else if (data.type === 'complete') {
                    setIsStreaming(false);
                    setStreamingResponse('');
                    // Refresh the conversation to get the complete messages
                    onSendMessage(currentMessage);
                    break;
                  } else if (data.type === 'error') {
                    console.error('Streaming error:', data.message);
                    setIsStreaming(false);
                    setStreamingResponse('');
                    // Fall back to regular message sending
                    await sendMessageFallback(currentMessage);
                    break;
                  }
                } catch (parseError) {
                  console.warn('Failed to parse streaming data:', line);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error with streaming:', error);
        setIsStreaming(false);
        setStreamingResponse('');
        // Fall back to regular message sending
        await sendMessageFallback(currentMessage);
      }
    }
  };

  // 🔧 FIXED: Fallback for non-streaming message sending using the API service
  const sendMessageFallback = async (messageContent: string) => {
    try {
      await onSendMessage(messageContent);
    } catch (error) {
      console.error('Error sending message (fallback):', error);
      // Could show user-friendly error message here
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMessage = e.target.value;
    setMessage(newMessage);
    
    if (coachingEnabled && newMessage.length > 15 && !isLoading) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      debounceTimerRef.current = setTimeout(async () => {
        try {
          setIsLoadingTip(true);
          const response = await conversationService.getCoachingTip(conversation._id, newMessage);
          setCoachingTip(response.data.data.tip);
        } catch (err) {
          console.error('Failed to get coaching tip:', err);
          setCoachingTip(null);
        } finally {
          setIsLoadingTip(false);
        }
      }, 1000);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages container with improved scrolling */}
      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 scroll-smooth"
        style={{ 
          scrollBehavior: 'smooth',
          overscrollBehavior: 'contain' // Prevent over-scrolling
        }}
      >
        {conversation.messages.map((msg: Message, index: number) => (
          <div 
            key={index} 
            className={`flex ${msg.role === SpeakerRole.STUDENT ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-3/4 p-3 rounded-lg ${
                msg.role === SpeakerRole.STUDENT 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-800'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              <p className={`text-xs mt-1 ${
                msg.role === SpeakerRole.STUDENT ? 'text-blue-200' : 'text-gray-500'
              }`}>
                {formatTimestamp(msg.timestamp)}
              </p>
            </div>
          </div>
        ))}
        
        {/* Streaming response */}
        {isStreaming && streamingResponse && (
          <div className="flex justify-start">
            <div className="max-w-3/4 p-3 rounded-lg bg-gray-200 text-gray-800">
              <p className="text-sm whitespace-pre-wrap">{streamingResponse}</p>
              {streamingResponse === 'Claude is thinking...' && (
                <div className="flex space-x-1 mt-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Scroll anchor */}
        <div ref={messagesEndRef} style={{ height: '1px' }} />
      </div>

      {/* Scroll to bottom button (appears when user has scrolled up) */}
      {userHasScrolled && !isNearBottom && (
        <div className="absolute bottom-24 right-4 z-10">
          <button
            onClick={forceScrollToBottom}
            className="bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        </div>
      )}

      {/* Coaching toggle */}
      <div className="px-4 pt-2">
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
            checked={coachingEnabled}
            onChange={() => {
              setCoachingEnabled(!coachingEnabled);
              setCoachingTip(null);
            }}
          />
          <span className="ml-2 text-sm text-gray-600">Enable real-time coaching</span>
        </label>
      </div>

      {/* Coaching tip */}
      {coachingEnabled && (coachingTip || isLoadingTip) && (
        <div className="px-4 pt-2">
          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-yellow-800">Coaching Tip:</h4>
                {isLoadingTip ? (
                  <p className="text-gray-600 text-sm mt-1">Analyzing your message...</p>
                ) : (
                  <p className="text-gray-700 text-sm mt-1">{coachingTip}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Input form */}
      <div className="p-4 border-t bg-white">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <input
            type="text"
            value={message}
            onChange={handleMessageChange}
            disabled={isLoading || isStreaming}
            placeholder={isStreaming ? "Claude is responding..." : "Type your message..."}
            className="flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={isLoading || isStreaming || !message.trim()}
            className={`px-4 py-2 rounded-md transition-colors ${
              isLoading || isStreaming || !message.trim()
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading || isStreaming ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ConversationChat;